import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { strapiListAllSamplingSchedulesForCalendar } from '../../../../api/strapiCrm';
import {
  MonthlyQuotationTeamCalendar,
  SurveyScheduleCalendarLegend,
  SurveyScheduleCalendarToolbar,
  type QuotationScheduleDaySummary,
} from '../../../../components/surveyScheduleCalendar';
import { useSurveyTeamsScheduleQuery, useIssuedBeritaAcaraWorkOrderIds } from '../../../../query/hooks';
import { currentMonthAnchor } from '../../../../sampling/surveyScheduleCalendar';
import { buildSamplingScheduleCalendarTeams } from '../../samplingScheduleCalendarModel';

type Props = {
  jwt: string | null;
};

export function SamplingSchedulesCalendarOverview({ jwt }: Props) {
  const navigate = useNavigate();
  const [monthAnchor, setMonthAnchor] = useState(currentMonthAnchor);

  const scheduleQ = useSurveyTeamsScheduleQuery(jwt, { userNumericId: null });
  const schedulesQ = useQuery({
    queryKey: ['strapi', 'samplingSchedulesForCalendarOverview', jwt],
    queryFn: () => strapiListAllSamplingSchedulesForCalendar(jwt!),
    enabled: Boolean(jwt),
  });
  const issuedQ = useIssuedBeritaAcaraWorkOrderIds(jwt);
  const teams = scheduleQ.data ?? [];
  const scheduleRows = schedulesQ.data ?? [];

  const { calendarTeams, dateOnlySummariesByDate } = useMemo(
    () => buildSamplingScheduleCalendarTeams(teams, scheduleRows),
    [teams, scheduleRows],
  );

  const openScheduleView = useCallback(
    (_date: string, summary: QuotationScheduleDaySummary) => {
      navigate(`/sampling/schedules/${encodeURIComponent(summary.scheduleDocumentId)}`);
    },
    [navigate],
  );

  if (scheduleQ.isPending || schedulesQ.isPending || issuedQ.isPending) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (scheduleQ.isError || schedulesQ.isError || issuedQ.isError) {
    return (
      <Alert severity="error">
        {scheduleQ.error instanceof Error
          ? scheduleQ.error.message
          : schedulesQ.error instanceof Error
            ? schedulesQ.error.message
            : issuedQ.error instanceof Error
              ? issuedQ.error.message
              : 'Gagal memuat kalender penjadwalan.'}
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Kalender tim survey semua quotation. Header kartu hijau = terjadwal, abu-abu = semua titik
        berita acara sudah terbit. Section bawah kuning jika petugas sudah dipilih, atau putih jika
        masih kosong.
      </Typography>

      <SurveyScheduleCalendarToolbar
        viewMode="monthly"
        onViewModeChange={() => undefined}
        weekStart=""
        onWeekStartChange={() => undefined}
        monthAnchor={monthAnchor}
        onMonthAnchorChange={setMonthAnchor}
        weekDates={[]}
        hideViewModeToggle
        monthlyTestId="sampling-schedules-cal-view-monthly"
      />

      <SurveyScheduleCalendarLegend mode="monthly" variant="received" />

      <MonthlyQuotationTeamCalendar
        monthAnchor={monthAnchor}
        teams={calendarTeams}
        extraSummariesByDate={dateOnlySummariesByDate}
        currentScheduleDocumentId=""
        cardTone="detail"
        useScheduleStatusColors={false}
        locked
        issuedWorkOrderIds={issuedQ.issuedWorkOrderIds}
        onQuotationClick={openScheduleView}
        getDayTestId={(date) => `sampling-schedules-cal-day-${date}`}
      />
    </Stack>
  );
}
